
import { useState, useEffect, useCallback } from "react"
import { useSelector } from "react-redux"
import { EmotionTrackersList } from "./emotion-trackers-list"
import { TrackerBuilder, TrackerBuilderProps } from "./tracker-builder"
import { TrackerDetail } from "./tracker-detail"
import { postOrgEqTracker, updateOrgEqTracker, getOrgEqTracker, getOrgEqTrackerHistory } from "../../api/helpers"

type IntelligenceView = "list" | "builder" | "detail"

type IntelligenceContainerProps = {} 

const transformTrackerForView = (apiTracker: any) => {
  if (!apiTracker) return null

  return {
    id: apiTracker.tracker_id || apiTracker.id,
    name: apiTracker.tracker_name || apiTracker.name || "Unnamed Tracker",
    description: apiTracker.description || "No description provided",
    visibility: apiTracker.visibility || "private",
    status: apiTracker.active ? "active" : "paused",
    sources: apiTracker.data_sources || apiTracker.sources || [],
    rules: {
      keywords: apiTracker.keywords || apiTracker.rules?.keywords || [],
      emotions: apiTracker.emotions || apiTracker.rules?.emotions || [],
      valenceRange: apiTracker.valence_range || apiTracker.rules?.valenceRange || [-1, 1],
      arousalRange: apiTracker.arousal_range || apiTracker.rules?.arousalRange || [0, 1],
    },
    actions: {
      notifications: apiTracker.notifications || apiTracker.actions?.notifications || [],
      cooldownSeconds:
        apiTracker.notification_cooldown || apiTracker.cooldownSeconds || apiTracker.actions?.cooldownSeconds || 300,
    },
    createdAt: apiTracker.created_at || apiTracker.createdAt || new Date().toISOString(),
    updatedAt: apiTracker.updated_at || apiTracker.updatedAt || new Date().toISOString(),
    version: apiTracker.version || 1,
  }
}

const transformTrackerHitsForDetail = (hits: any[]): any[] => {
  if (!Array.isArray(hits)) return []

  return hits.map((hit, index) => {
    const matchedAt = hit?.matchedAt || hit?.matched_at || hit?.timestamp || new Date().toISOString()
    const source = hit?.source || hit?.channel || hit?.metadata?.source || "unknown"
    const snippet = hit?.snippet || hit?.text || hit?.message || ""

    const rawEmotions = Array.isArray(hit?.triggeredEmotions)
      ? hit.triggeredEmotions
      : Array.isArray(hit?.emotions)
        ? hit.emotions
        : Array.isArray(hit?.triggered_emotions)
          ? hit.triggered_emotions
          : []

    const triggeredEmotions = rawEmotions.map((emotion: any) => ({
      name: emotion?.name || emotion?.emotion || "Emotion",
      value: typeof emotion?.value === "number" ? emotion.value : typeof emotion?.score === "number" ? emotion.score : 0,
      threshold:
        typeof emotion?.threshold === "number"
          ? emotion.threshold
          : typeof emotion?.minThreshold === "number"
            ? emotion.minThreshold
            : typeof emotion?.thresholdValue === "number"
              ? emotion.thresholdValue
              : 0,
    }))

    const participants =
      Array.isArray(hit?.metadata?.participants) && hit.metadata.participants.length > 0
        ? hit.metadata.participants
        : Array.isArray(hit?.participants)
          ? hit.participants
          : undefined

    return {
      id: hit?.id || hit?.match_id || `hit-${index}`,
      matchedAt,
      source,
      snippet,
      triggeredEmotions,
      valence:
        typeof hit?.valence === "number"
          ? hit.valence
          : typeof hit?.valence_score === "number"
            ? hit.valence_score
            : typeof hit?.emotion_valence === "number"
              ? hit.emotion_valence
              : 0,
      arousal:
        typeof hit?.arousal === "number"
          ? hit.arousal
          : typeof hit?.arousal_score === "number"
            ? hit.arousal_score
            : typeof hit?.emotion_arousal === "number"
              ? hit.emotion_arousal
              : 0,
      metadata: {
        channel: hit?.metadata?.channel || hit?.channel,
        participants,
        duration:
          typeof hit?.metadata?.duration === "number"
            ? hit.metadata.duration
            : typeof hit?.duration === "number"
              ? hit.duration
              : undefined,
      },
    }
  })
}

export function IntelligenceContainer({}: IntelligenceContainerProps) {
  const [currentView, setCurrentView] = useState<IntelligenceView>("list")
  const [selectedTrackerId, setSelectedTrackerId] = useState<string | null>(null)
  const [selectedTracker, setSelectedTracker] = useState<any>(null)
  const [editingTrackerId, setEditingTrackerId] = useState<string | null>(null)
  const [editingTracker, setEditingTracker] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [trackerHits, setTrackerHits] = useState<any[]>([])

  const user = useSelector((state: any) => state.auth.user)
  const organizationId = user?.organization_info?._id

  // Handle sub-hash routing for tracker views
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash

      if (hash.startsWith("#trackers")) {
        const params = new URLSearchParams(hash.split("&").slice(1).join("&"))
        const view = params.get("view") || "list"
        const trackerId = params.get("id")

        if (view === "list") {
          setCurrentView("list")
          setSelectedTrackerId(null)
          setSelectedTracker(null)
          setEditingTrackerId(null)
          setEditingTracker(null)
          setDetailLoading(false)
          setDetailError(null)
          setTrackerHits([])
        } else if (view === "create") {
          setCurrentView("builder")
          setEditingTrackerId(null)
          setEditingTracker(null)
          setDetailLoading(false)
          setDetailError(null)
          setTrackerHits([])
        } else if (view === "detail" && trackerId) {
          setCurrentView("detail")
          setSelectedTrackerId(trackerId)
          setDetailError(null)
          // Reset the cached tracker if the ID changes so we fetch fresh data
          setSelectedTracker((prev: any) => {
            if (!prev) return null
            const prevId = prev.id || prev.tracker_id
            return prevId === trackerId ? prev : null
          })
          setTrackerHits((prev: any[]) => {
            if (!prev?.length) return []
            const first = prev[0]
            return first?.tracker_id === trackerId ? prev : []
          })
        } else if (view === "edit" && trackerId) {
          setCurrentView("builder")
          setEditingTrackerId(trackerId)
          // editingTracker will be set by handleEditTracker
          setDetailLoading(false)
          setDetailError(null)
          setTrackerHits([])
        }
      }
    }

    // Set initial view based on current hash
    handleHashChange()

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange)

    return () => {
      window.removeEventListener("hashchange", handleHashChange)
    }
  }, [])

  const updateHash = (view: string, trackerId?: string) => {
    let newHash = "#trackers"
    if (view !== "list") {
      newHash += `&view=${view}`
      if (trackerId) {
        newHash += `&id=${trackerId}`
      }
    }
    window.location.hash = newHash
  }

  const handleCreateTracker = () => {
    updateHash("create")
  }

  const handleEditTracker = (trackerId: string, tracker?: any) => {
    setEditingTracker(tracker || null)
    updateHash("edit", trackerId)
  }

  const handleViewTracker = (trackerId: string, tracker?: any) => {
    const transformedTracker = transformTrackerForView(tracker)
    setSelectedTracker(transformedTracker) // Set tracker data immediately for better UX
    setDetailError(null)
    setDetailLoading(false)
    setTrackerHits([])
    updateHash("detail", trackerId)
    // Scroll to top when viewing tracker details
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBackToList = () => {
    updateHash("list")
    // Scroll to top when navigating back to list
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSaveTracker = async (trackerData: any) => {
    if (!user?.organization_info?._id) {
      setError("Organization ID not found. Please try refreshing the page.")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      console.log("Saving tracker with data:", trackerData)
      
      // Map the form data to the API format
      const dataSources = []
      if (trackerData.sources.includes("slack")) dataSources.push("chats")
      if (trackerData.sources.includes("gmail")) dataSources.push("emails")
      if (trackerData.sources.includes("zoom")) dataSources.push("calls")

      // Map visibility from form format to API format
      let apiVisibility: "public" | "private" | "team" = "private"
      if (trackerData.visibility === "organization") {
        apiVisibility = "public"
      } else if (trackerData.visibility === "team") {
        apiVisibility = "team"
      } else if (trackerData.visibility === "private") {
        apiVisibility = "private"
      }

      // Build API data object - only include essential fields
      const apiData: any = {
        name: trackerData.name,
        description: trackerData.description || "No description",
        visibility: apiVisibility,
        data_sources: dataSources,
      }

      // Only add optional fields if they have meaningful values
      if (trackerData.teams && trackerData.teams.length > 0) {
        apiData.org_team_ids = trackerData.teams
      }
      
      if (trackerData.keywords && trackerData.keywords.length > 0) {
        apiData.keywords = trackerData.keywords
      }
      
      // Notifications
      const notifications = []
      if (trackerData.notificationInApp) notifications.push("in_app")
      if (trackerData.notificationEmail) notifications.push("email")
      if (trackerData.notificationSlack) notifications.push("slack")
      if (notifications.length > 0) {
        apiData.notifications = notifications
      }
      if (trackerData.cooldownSeconds) {
        apiData.notification_cooldown = trackerData.cooldownSeconds
      }
      
      // Timing constraints
      if (trackerData.startDate) {
        apiData.start_date = trackerData.startDate
      }
      if (trackerData.endDate) {
        apiData.end_date = trackerData.endDate
      }
      if (trackerData.eventDate) {
        apiData.event_date = trackerData.eventDate
      }

      console.log("Sending API data:", apiData)
      console.log("Editing tracker ID:", editingTrackerId)
      
      let response
      if (editingTrackerId) {
        // Update existing tracker
        console.log("Updating existing tracker:", editingTrackerId)
        response = await updateOrgEqTracker(user.organization_info._id, editingTrackerId, apiData)
      } else {
        // Create new tracker
        console.log("Creating new tracker")
        response = await postOrgEqTracker(user.organization_info._id, apiData)
      }
      
      console.log("API response:", response)

      if (response) {
        setRefreshKey(prev => prev + 1) // Force refresh of tracker list
        updateHash("list") // Navigate back to list
        // Could show success message here
      }
    } catch (err: any) {
      console.error("Save tracker error:", err)
      const action = editingTrackerId ? "update" : "create"
      setError(err.response?.data?.message || `Failed to ${action} tracker. Please try again.`)
    } finally {
      setIsLoading(false)
    }
  }

  // console.log("IntelligenceContainer render - currentView:", currentView, "user:", user?.organization_info?._id)

  const fetchTrackerDetail = useCallback(
    async (trackerId: string) => {
      if (!organizationId) {
        setDetailError("Organization ID not found. Please refresh and try again.")
        setDetailLoading(false)
        return
      }

      setDetailLoading(true)
      setDetailError(null)

      try {
        const [detailResponse, hitsResponse] = await Promise.all([
          getOrgEqTracker(organizationId, trackerId),
          getOrgEqTrackerHistory(organizationId, trackerId, 20),
        ])

        const trackerData = transformTrackerForView(detailResponse?.tracker || detailResponse)
        setSelectedTracker(trackerData)

        const hitsPayload =
          hitsResponse?.history ||
          hitsResponse?.hits ||
          hitsResponse?.items ||
          hitsResponse?.data ||
          []
        setTrackerHits(transformTrackerHitsForDetail(hitsPayload))
      } catch (err: any) {
        console.error("Failed to load tracker detail:", err)
        setDetailError(err?.response?.data?.message || "Failed to load tracker details.")
        setSelectedTracker(null)
        setTrackerHits([])
      } finally {
        setDetailLoading(false)
      }
    },
    [organizationId],
  )

  useEffect(() => {
    if (currentView !== "detail" || !selectedTrackerId) return

    const currentId = selectedTracker?.id || selectedTracker?.tracker_id
    if (currentId === selectedTrackerId && trackerHits.length > 0) return

    fetchTrackerDetail(selectedTrackerId)
  }, [currentView, selectedTrackerId, selectedTracker, trackerHits.length, fetchTrackerDetail])

  return (
    <div className="min-h-screen">
      {currentView === "list" && (
        <div>
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Agentic Emotion Trackers</h1>
            <p className="text-muted-foreground">
              Mine data across systems to associate behaviors, attitudes and topics with emotional data
            </p>
          </div>
          <EmotionTrackersList
            key={refreshKey}
            onCreateTracker={handleCreateTracker}
            onViewTracker={handleViewTracker}
            onEditTracker={handleEditTracker}
          />
        </div>
      )}

      {currentView === "builder" && (
        <div>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}
          <TrackerBuilder
            trackerId={editingTrackerId}
            tracker={editingTracker}
            onSave={handleSaveTracker}
            onCancel={handleBackToList}
            isLoading={isLoading}
          />
        </div>
      )}

      {currentView === "detail" && selectedTrackerId && (
        <TrackerDetail
          trackerId={selectedTrackerId}
          tracker={selectedTracker}
          hits={trackerHits}
          isLoading={detailLoading}
          error={detailError}
          onRetry={() => fetchTrackerDetail(selectedTrackerId)}
          onEdit={() => handleEditTracker(selectedTrackerId)}
          onBack={handleBackToList}
        />
      )}
    </div>
  )
}